import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Building2, User, Calendar, DollarSign, XCircle, FileText, Trash2, Eye, Download } from "lucide-react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { resolveFileUrl, downloadFile } from "@/api/base44Client";

function isPdfUrl(url) {
  if (!url) return false;
  if (url.startsWith('data:')) return url.includes('application/pdf');
  return url.toLowerCase().endsWith('.pdf') || url.includes('application/pdf');
}

export default function DefaultedSaleCard(props) {
  // Extract known non-sale props
  const { client, apartment, onDelete } = props;
  
  // 🛡️ ROBUST DATA FETCHING:
  // 1. Check if passed as 'defaultedSale'
  // 2. Check if passed as 'sale'
  // 3. Fallback to the entire props object (handles case where parent spreads {...sale})
  const defaultedSale = props.defaultedSale || props.sale || props;

  const { canEdit, canExport } = useAuth();
  const [showContractView, setShowContractView] = useState(false);
  const [contractDisplayUrl, setContractDisplayUrl] = useState(null);
  const [contractLoadError, setContractLoadError] = useState(null);

  useEffect(() => {
    if (!showContractView || !defaultedSale?.contract_document_url) {
      if (!showContractView) {
        setContractDisplayUrl(null);
        setContractLoadError(null);
      }
      return;
    }

    const rawUrl = defaultedSale.contract_document_url;
    if (rawUrl.startsWith('data:')) {
      setContractDisplayUrl(rawUrl);
      setContractLoadError(null);
      return;
    }

    setContractDisplayUrl(resolveFileUrl(rawUrl));
    setContractLoadError(null);
  }, [showContractView, defaultedSale?.contract_document_url]);

  // Safety check: if we still don't have a valid sale object with an ID, don't render
  if (!defaultedSale || (!defaultedSale.id && !defaultedSale._id && !defaultedSale.unit_number && !defaultedSale.total_price)) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="hover:shadow-lg transition-all duration-300 border-0 shadow-md border-l-4 border-l-red-500">
        <CardContent className="p-6">
          <div className="space-y-4">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="flex items-start gap-4 flex-1">
                <div className="w-14 h-14 bg-gradient-to-br from-red-600 to-red-700 rounded-lg flex items-center justify-center">
                  <XCircle className="w-7 h-7 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-xl font-bold text-slate-900">
                      Unit {defaultedSale?.unit_number || apartment?.unit_number || 'N/A'}
                    </h3>
                    <Badge className="bg-red-100 text-red-700 border-red-200 border">
                      Defaulted
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <User className="w-4 h-4" />
                    <span className="font-medium">{client?.full_name || 'Unknown Client'}</span>
                  </div>
                </div>
              </div>
              {canEdit && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => onDelete(defaultedSale)}
                  className="flex-shrink-0"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              )}
            </div>

            {/* Financial Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Total Price</p>
                <p className="text-lg font-bold text-slate-900">
                  {defaultedSale.total_price?.toLocaleString()}
                </p>
                <p className="text-xs text-slate-600">USD</p>
              </div>
              <div className="bg-red-50 p-3 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Total Paid</p>
                <p className="text-lg font-bold text-red-600">
                  {(defaultedSale.total_paid || 0).toLocaleString()}
                </p>
                <p className="text-xs text-slate-600">USD</p>
              </div>
              <div className="bg-amber-50 p-3 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Monthly</p>
                <p className="text-lg font-bold text-amber-600">
                  {(defaultedSale.monthly_payment || 0).toLocaleString()}
                </p>
                <p className="text-xs text-slate-600">USD</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Duration</p>
                <p className="text-lg font-bold text-blue-600">
                  {defaultedSale.payment_duration_months || 0}
                </p>
                <p className="text-xs text-slate-600">months</p>
              </div>
            </div>

            {/* Defaulted Information */}
            {defaultedSale.defaulted_reason && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-red-900 mb-1">Reason for Default</p>
                    <p className="text-sm text-red-700">{defaultedSale.defaulted_reason}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Additional Details */}
            <div className="flex flex-wrap gap-4 text-sm text-slate-600 pt-2 border-t items-center">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <span>
                  Sale Date: {defaultedSale.sale_date ? format(new Date(defaultedSale.sale_date), 'MMM d, yyyy') : 'N/A'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-400" />
                <span>
                  Defaulted: {defaultedSale.defaulted_date ? format(new Date(defaultedSale.defaulted_date), 'MMM d, yyyy') : 'N/A'}
                </span>
              </div>
              {defaultedSale.contract_number && (
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-400" />
                  <span>Contract: {defaultedSale.contract_number}</span>
                </div>
              )}
              {defaultedSale.contract_document_url && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowContractView(true)}
                  className="gap-2"
                >
                  <Eye className="w-4 h-4" />
                  View Contract
                </Button>
              )}
            </div>

            {defaultedSale.notes && (
              <div className="text-sm text-slate-600 pt-2 border-t">
                <p className="font-medium text-slate-700 mb-1">Notes:</p>
                <p>{defaultedSale.notes}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {defaultedSale.contract_document_url && (
        <Dialog open={showContractView} onOpenChange={setShowContractView}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Contract #{defaultedSale.contract_number} — {client?.full_name || 'Client'}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 min-h-0 overflow-auto border rounded-lg bg-slate-50">
              {contractLoadError ? (
                <div className="p-8 text-center text-slate-600">
                  <p className="font-medium">{contractLoadError}</p>
                  <p className="text-sm mt-2">The file may have been removed or the server is unavailable.</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={() => {
                      if (defaultedSale.contract_document_url && !defaultedSale.contract_document_url.startsWith('data:')) {
                        window.open(resolveFileUrl(defaultedSale.contract_document_url), '_blank', 'noopener,noreferrer');
                      }
                    }}
                  >
                    Try opening in new tab
                  </Button>
                </div>
              ) : contractDisplayUrl ? (
                isPdfUrl(defaultedSale.contract_document_url) ? (
                  <iframe
                    src={contractDisplayUrl}
                    title="Contract Document"
                    className="w-full h-[70vh] min-h-[400px]"
                  />
                ) : (
                  <img
                    src={contractDisplayUrl}
                    alt="Contract Document"
                    className="w-full h-auto max-h-[70vh] object-contain"
                    onError={() => {
                      setContractDisplayUrl(null);
                      setContractLoadError('Failed to load');
                    }}
                  />
                )
              ) : (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto" />
                  <p className="text-sm text-slate-500 mt-2">Loading document...</p>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowContractView(false)}>
                Close
              </Button>
              {canExport && (
                <Button
                  type="button"
                  onClick={async () => {
                    const url = resolveFileUrl(defaultedSale.contract_document_url);
                    const ext = isPdfUrl(defaultedSale.contract_document_url) ? 'pdf' : 'png';
                    const filename = `contract-${defaultedSale.contract_number?.replace(/\s+/g, '-') || 'document'}.${ext}`;
                    await downloadFile(url, filename);
                  }}
                  className="bg-gradient-to-r from-slate-900 to-slate-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </motion.div>
  );
}